import { useState } from "react";
import { Upload, FileText, Video, Trash2, Edit2, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { trpc } from "@/utils/trpc";

const CATEGORIES = [
  "Policies",
  "SOPs",
  "Technical Guides",
  "HR & Onboarding",
  "Safety",
  "General",
];

const CONTENT_TYPES = ["PDF", "Video"];
const MAX_UPLOAD_SIZE_BYTES = 45 * 1024 * 1024;

async function fileToBase64(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      const [, base64Data = ""] = result.split(",", 2);
      resolve(base64Data);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export default function AdminKnowledgeCenter() {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    contentType: "",
    tags: "",
    mandatoryAck: false,
    visibleToRoles: [] as string[],
  });
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch content list
  const { data: contents = [], isLoading, refetch } = trpc.knowledge.getLibrary.useQuery();

  // Create content mutation
  const createContentMutation = trpc.knowledge.createContent.useMutation({
    onSuccess: () => {
      refetch();
      setFormData({
        title: "",
        description: "",
        category: "",
        contentType: "",
        tags: "",
        mandatoryAck: false,
        visibleToRoles: [],
      });
      setFile(null);
      alert("Content uploaded successfully!");
    },
    onError: (error) => {
      alert(`Error: ${error.message}`);
    },
  });

  // Delete content mutation
  const deleteContentMutation = trpc.knowledge.deleteContent.useMutation({
    onSuccess: () => {
      refetch();
      alert("Content deleted successfully!");
    },
    onError: (error) => {
      alert(`Error: ${error.message}`);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.[0]) {
      if (files[0].size > MAX_UPLOAD_SIZE_BYTES) {
        alert("File is too large. Please upload a file smaller than 45MB.");
        e.target.value = "";
        return;
      }
      setFile(files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !formData.title || !formData.category || !formData.contentType) {
      alert("Please fill in all required fields");
      return;
    }

    setIsUploading(true);

    try {
      const base64Data = await fileToBase64(file);

      await createContentMutation.mutateAsync({
        title: formData.title,
        description: formData.description,
        category: formData.category,
        contentType: formData.contentType as "PDF" | "Video",
        tags: formData.tags,
        upload: {
          fileName: file.name,
          mimeType: file.type || (formData.contentType === "PDF" ? "application/pdf" : "video/mp4"),
          base64Data,
        },
        mandatoryAck: formData.mandatoryAck,
        visibleToRoles: formData.visibleToRoles,
      });
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload content");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this content?")) {
      deleteContentMutation.mutate({ id });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Knowledge Center Admin</h1>
        <p className="text-muted-foreground mt-2">Manage content, PDFs, and videos</p>
      </div>

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload New Content
          </CardTitle>
          <CardDescription>Add PDFs, videos, and documents to the knowledge base</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">
                  Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="title"
                  placeholder="e.g., Security Policy 2024"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  disabled={isUploading}
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">
                  Category <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                  disabled={isUploading}
                >
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Content Type */}
              <div className="space-y-2">
                <Label htmlFor="contentType">
                  Content Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.contentType}
                  onValueChange={(value) => setFormData({ ...formData, contentType: value })}
                  disabled={isUploading}
                >
                  <SelectTrigger id="contentType">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label htmlFor="tags">Tags</Label>
                <Input
                  id="tags"
                  placeholder="e.g., security, important, required"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  disabled={isUploading}
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe the content..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                disabled={isUploading}
              />
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="file">
                File <span className="text-red-500">*</span>
              </Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors">
                <input
                  id="file"
                  name="file"
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.mp4,.webm,.mov"
                  className="hidden"
                  required
                  disabled={isUploading}
                />
                <label htmlFor="file" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Click to upload or drag and drop</p>
                      <p className="text-sm text-muted-foreground">PDF, MP4, WebM, MOV up to 45MB</p>
                    </div>
                  </div>
                  {file && <p className="text-sm text-green-600 mt-2">✓ {file.name}</p>}
                </label>
              </div>
            </div>

            {/* Mandatory Acknowledgement */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="mandatory"
                checked={formData.mandatoryAck}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, mandatoryAck: checked as boolean })
                }
                disabled={isUploading}
              />
              <Label htmlFor="mandatory" className="font-normal cursor-pointer">
                Mark as mandatory acknowledgement (users must acknowledge before accessing)
              </Label>
            </div>

            {/* Submit Button */}
            <Button type="submit" className="w-full" disabled={isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Content
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Content List */}
      <Card>
        <CardHeader>
          <CardTitle>Published Content</CardTitle>
          <CardDescription>{contents.length} items in knowledge base</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Title</th>
                    <th className="text-left py-3 px-4 font-medium">Category</th>
                    <th className="text-left py-3 px-4 font-medium">Type</th>
                    <th className="text-left py-3 px-4 font-medium">Status</th>
                    <th className="text-left py-3 px-4 font-medium">Created</th>
                    <th className="text-left py-3 px-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contents.map((content: any) => (
                    <tr key={content.id} className="border-b hover:bg-accent/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {content.contentType === "PDF" ? (
                            <FileText className="h-4 w-4 text-red-500" />
                          ) : (
                            <Video className="h-4 w-4 text-blue-500" />
                          )}
                          {content.title}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{content.category}</Badge>
                      </td>
                      <td className="py-3 px-4">{content.contentType}</td>
                      <td className="py-3 px-4">
                        {content.status === "PUBLISHED" ? (
                          <Badge className="bg-green-100 text-green-800">Published</Badge>
                        ) : (
                          <Badge variant="secondary">{content.status}</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {new Date(content.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                            onClick={() => handleDelete(content.id)}
                            disabled={deleteContentMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {contents.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No content uploaded yet. Create your first knowledge base entry above.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
